import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { Video, Play, Download, Trash2, Calendar, MapPin, User as UserIcon, Clock } from 'lucide-react';
import { footageService } from '../services/FootageService';
import { FootageFile } from '../models/Footage';

// Group footage into collapsible "folders" by uploader or by month.
function groupFootage(
  items: FootageFile[],
  mode: 'person' | 'month'
): Array<{ key: string; label: string; items: FootageFile[] }> {
  const map = new Map<string, { label: string; items: FootageFile[] }>();
  for (const item of items) {
    let key: string;
    let label: string;
    if (mode === 'person') {
      key = item.uploadedBy || 'unknown';
      label = item.uploadedByName || 'Unknown';
    } else {
      const d = item.uploadDate instanceof Date ? item.uploadDate : new Date(item.uploadDate as any);
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (!map.has(key)) map.set(key, { label, items: [] });
    map.get(key)!.items.push(item);
  }
  const groups = Array.from(map.entries()).map(([key, v]) => ({ key, label: v.label, items: v.items }));
  groups.forEach((g) =>
    g.items.sort(
      (a, b) => new Date(b.uploadDate as any).getTime() - new Date(a.uploadDate as any).getTime()
    )
  );
  groups.sort((a, b) => (mode === 'month' ? b.key.localeCompare(a.key) : a.label.localeCompare(b.label)));
  return groups;
}

export const Footage: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const [footage, setFootage] = useState<FootageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFootage, setSelectedFootage] = useState<FootageFile | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  // Folder grouping for the library view.
  const [groupBy, setGroupBy] = useState<'person' | 'month'>('person');

  useEffect(() => {
    loadFootage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const loadFootage = async () => {
    if (!company) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await footageService.getFootageByCompany(company.id, 100);
      setFootage(data);
      setError(null);
    } catch (err) {
      console.error('Error loading footage:', err);
      setError('Failed to load footage');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleView = (item: FootageFile) => {
    setSelectedFootage(item);
    setVideoLoading(true);
    setVideoError(null);
    footageService.incrementReviewCount(item.id);
  };

  const handleDelete = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to archive this footage?')) {
      return;
    }

    try {
      await footageService.deleteFootage(id);
      await loadFootage();
    } catch (err) {
      console.error('Error deleting footage:', err);
      // eslint-disable-next-line no-alert
      alert('Failed to delete footage');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <div style={{ marginBottom: 'var(--spacing-8)' }}>
          <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Footage Library</h1>
          <p style={{ color: 'var(--color-gray-600)' }}>
            View and manage uploaded body camera footage
          </p>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-6)' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
              Loading footage...
            </p>
          </div>
        ) : footage.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
            <Video size={48} style={{ margin: '0 auto var(--spacing-4)', color: 'var(--color-gray-400)' }} />
            <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--spacing-2)' }}>
              No footage uploaded yet
            </h3>
            <p style={{ color: 'var(--color-gray-600)' }}>
              Upload your first video to get started
            </p>
          </div>
        ) : (
          <>
            {/* Folder grouping toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Folders:</span>
              <button
                className={`btn ${groupBy === 'person' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setGroupBy('person')}
              >
                By person
              </button>
              <button
                className={`btn ${groupBy === 'month' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setGroupBy('month')}
              >
                By month
              </button>
            </div>
            {groupFootage(footage, groupBy).map((group) => (
              <details key={group.key} open style={{ marginBottom: 'var(--spacing-4)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-lg)', padding: 'var(--spacing-2) 0' }}>
                  {group.label} ({group.items.length})
                </summary>
                <div className="grid grid-cols-3" style={{ gap: 'var(--spacing-6)', marginTop: 'var(--spacing-3)' }}>
                  {group.items.map((item) => (
                    <div key={item.id} className="card">
                <div
                  style={{
                    backgroundColor: 'var(--color-gray-900)',
                    borderRadius: 'var(--radius-lg)',
                    aspectRatio: '16/9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 'var(--spacing-4)',
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleView(item)}
                >
                  <Video size={48} style={{ color: 'white', opacity: 0.6 }} />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'var(--spacing-3)',
                      right: 'var(--spacing-3)',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: 'var(--spacing-1) var(--spacing-2)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    {item.duration > 0 ? formatDuration(item.duration) : '--:--'}
                  </div>
                  <button
                    style={{
                      position: 'absolute',
                      bottom: 'var(--spacing-3)',
                      right: 'var(--spacing-3)',
                      backgroundColor: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(item);
                    }}
                  >
                    <Play size={20} fill="white" />
                  </button>
                </div>

                <h3
                  style={{
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-semibold)',
                    marginBottom: 'var(--spacing-2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={item.originalFilename}
                >
                  {item.originalFilename}
                </h3>

                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-1)' }}>
                    <UserIcon size={14} />
                    <span>{item.uploadedByName}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-1)' }}>
                    <Calendar size={14} />
                    <span>{formatDate(item.uploadDate)}</span>
                  </div>
                  {item.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-1)' }}>
                      <MapPin size={14} />
                      <span>{item.location.name}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <Clock size={14} />
                    <span>{formatFileSize(item.fileSize)}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                  <a
                    href={item.fileUrl}
                    download={item.originalFilename}
                    className="btn btn-secondary"
                    style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download size={16} style={{ display: 'inline', marginRight: 'var(--spacing-1)' }} />
                    Download
                  </a>
                  {(user.role === 'admin' || user.id === item.uploadedBy) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      className="btn btn-secondary"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
                  ))}
                </div>
              </details>
            ))}
          </>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedFootage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--spacing-6)',
          }}
          onClick={() => {
            setSelectedFootage(null);
            setVideoLoading(false);
            setVideoError(null);
          }}
        >
          <div
            style={{
              maxWidth: '1200px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: 'var(--spacing-4)', color: 'white' }}>
              <h2 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--spacing-2)' }}>
                {selectedFootage.originalFilename}
              </h2>
              <div style={{ display: 'flex', gap: 'var(--spacing-4)', fontSize: 'var(--text-sm)', opacity: 0.8 }}>
                <span>Uploaded by {selectedFootage.uploadedByName}</span>
                <span>{formatDate(selectedFootage.uploadDate)}</span>
                {selectedFootage.location && <span>{selectedFootage.location.name}</span>}
              </div>
            </div>

            {/* Video Loading State */}
            {videoLoading && (
              <div
                style={{
                  width: '100%',
                  aspectRatio: '16/9',
                  backgroundColor: 'black',
                  borderRadius: 'var(--radius-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 'var(--spacing-4)',
                }}
              >
                <div style={{ textAlign: 'center', color: 'white' }}>
                  <div className="spinner" style={{ margin: '0 auto var(--spacing-3)' }} />
                  <p>Loading video...</p>
                </div>
              </div>
            )}

            {/* Video Error State */}
            {videoError && (
              <div
                className="alert alert-error"
                style={{ marginBottom: 'var(--spacing-4)' }}
              >
                {videoError}
              </div>
            )}

            {/* Video Player */}
            <video
              src={selectedFootage.fileUrl}
              controls
              autoPlay
              playsInline
              style={{
                width: '100%',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: 'black',
                display: videoLoading ? 'none' : 'block',
              }}
              onLoadedData={() => setVideoLoading(false)}
              onError={(e) => {
                setVideoLoading(false);
                setVideoError('Failed to load video. The file may be corrupted or the URL may have expired.');
                console.error('Video error:', e);
              }}
            />

            <button
              onClick={() => {
                setSelectedFootage(null);
                setVideoLoading(false);
                setVideoError(null);
              }}
              className="btn btn-secondary"
              style={{ marginTop: 'var(--spacing-4)' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};
